import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createSphere,
  createToken,
  getConnections,
  getProactiveSettings,
  listAudit,
  listProjects,
  listSpheres,
  revokeClient,
  revokeToken,
  startConnector,
  updateProactiveSettings,
  updateSphere,
} from '../lifeos/api.js';
import { auditLabel, since, stamp, stampWithYear, ymdInHomeZone } from '../lifeos/format.js';
import { isClock, settingsPatch } from '../lifeos/settings.js';
import { resetSphereCache, sphereColor, sphereName, useSpheres } from '../lifeos/spheres.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { Collapsible, CopyButton, Loader, Segmented } from '../lifeos/ui.js';
import { Button, EmptyState, Group, Icon, IconTile, Page, PickSheet, Row, Sheet } from '../ui/index.js';
import {
  addCaldav,
  addIcs,
  addImap,
  canWrite,
  defaultImapHost,
  getConnectors,
  isCalendar,
  isMail,
  microsoftStartUrl,
  nav,
  providerName,
  removeConnector,
  statusText,
  updateConnector,
  type Connector,
  type ConnectorPatch,
} from '../lib/connectors.js';
import { useBusy } from '../lifeos/useBusy.js';
import { useLoad, type Loaded } from '../lifeos/useLoad.js';
import type {
  ConnectionsPayload,
  ConnectorsPayload,
  GrantableScope,
  OAuthClientRow,
  ProactiveSettings,
  Project,
  Sphere,
  TokenRecord,
  TokenRow,
} from '../lifeos/types.js';

const SCOPES: readonly { key: GrantableScope; label: string; hint: string }[] = [
  { key: 'read', label: 'Read', hint: 'see tasks, people, money, health' },
  { key: 'write', label: 'Write', hint: 'add and change tasks and notes' },
  { key: 'act', label: 'Act', hint: 'start agent runs, reject approvals' },
];

const EXPIRY: readonly { days: number | null; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: null, label: 'Never' },
];

type Tab = 'claude' | 'accounts' | 'activity';
const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'claude', label: 'Claude' },
  { value: 'accounts', label: 'Accounts & alerts' },
  { value: 'activity', label: 'Activity' },
];

/**
 * Connections (`/connections`), three tabs in `?tab=`: Claude (the MCP URL,
 * personal access tokens and connected apps), Accounts & alerts (mail and
 * calendar accounts, spheres, how often Vitals may ping) and Activity (recent
 * access). The Google connect callback (`?connector=…`) opens Accounts.
 *
 * Revoking is the one state change here without an undo window: it is sent
 * at once, and the row goes only when the server confirms. A credential that
 * may have leaked must be dead the moment the page says "Revoked", not five
 * seconds later or never (sign-out, closed tab).
 */
export default function Connections(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: Tab = params.get('connector') ? 'accounts' : (TABS.find((t) => t.value === requested)?.value ?? 'claude');
  const spheres = useSpheres();
  const projects = useLoad(() => listProjects(), []);
  const connections = useLoad(getConnections, []);
  const mcpUrl = `${window.location.origin}/mcp`;

  return (
    <Page title="Connections" className="bc-conn">
      <Segmented
        label="Connections"
        value={tab}
        options={TABS}
        onChange={(t) =>
          setParams(
            (p) => {
              const n = new URLSearchParams(p);
              if (t === 'claude') n.delete('tab');
              else n.set('tab', t);
              return n;
            },
            { replace: true },
          )
        }
      />

      {tab === 'claude' && (
        <>
          <section className="ui-card bc-mcp" aria-labelledby="conn-claude">
            <div className="bc-mcp__head">
              <span className="bc-mcp__mark" aria-hidden="true">
                <Icon name="bolt" size={20} />
              </span>
              <div>
                <h2 className="ui-card__title bc-mcp__title" id="conn-claude">
                  Connect Claude
                </h2>
                <p className="bc-mcp__sub">Let Claude read and update Vitals for you.</p>
              </div>
            </div>
            <div className="lo-mcp bc-mcp__url">
              <code className="lo-mcp__url">{mcpUrl}</code>
              <CopyButton text={mcpUrl} label="Copy URL" />
            </div>
            <ol className="lo-steps bc-steps">
              <li>In Claude, open Settings → Connectors → Add custom connector.</li>
              <li>Paste the URL. Claude sends you here to sign in and choose what it may see.</li>
              <li>
                For Claude Code, create a token below and run{' '}
                <code className="lo-inline-code">
                  claude mcp add --transport http vitals {mcpUrl} --header "Authorization: Bearer vtp_…"
                </code>
              </li>
            </ol>
          </section>
          <Tokens state={connections} spheres={spheres} projects={projects.data ?? []} />
          <Clients state={connections} />
        </>
      )}
      {tab === 'accounts' && (
        <>
          <Accounts />
          <Spheres />
          <Notifications />
        </>
      )}
      {tab === 'activity' && <RecentAccess />}
    </Page>
  );
}

// --- Personal access tokens ----------------------------------------------------------------------

function Tokens({
  state,
  spheres,
  projects,
}: {
  state: Loaded<ConnectionsPayload>;
  spheres: Sphere[];
  projects: Project[];
}): JSX.Element {
  const toast = useToast();
  const [created, setCreated] = useState<{ token: string; record: TokenRecord } | null>(null);
  const [revoked, setRevoked] = useState<ReadonlySet<string>>(new Set());
  const revoking = useBusy<string>();

  function revoke(t: TokenRow): Promise<void> {
    return revoking.run(t.id, async () => {
      try {
        await revokeToken(t.id);
        setRevoked((s) => new Set(s).add(t.id));
        toast.info(`Revoked ${t.label}`);
        void state.reload();
      } catch (err) {
        toast.error(`Couldn't revoke ${t.label}: ${errorText(err)}`);
      }
    });
  }

  const projectName = (id: string): string => projects.find((p) => p.id === id)?.name ?? 'project';

  return (
    <section className="ui-groupwrap" aria-labelledby="conn-tokens">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="conn-tokens">
          Access tokens
        </h2>
      </div>
      {created && (
        <div className="ui-card lo-secret bc-secret" role="region" aria-label="New token">
          <div className="ui-card__eyebrow bc-warn">
            <Icon name="alert" size={16} /> Shown once
          </div>
          <h3 className="ui-card__title">Copy this token now</h3>
          <p className="bc-secret__body">Vitals keeps only a hash, so it can't be shown again.</p>
          <div className="lo-mcp bc-mcp__url">
            <code className="lo-mcp__url lo-secret__token">{created.token}</code>
            <CopyButton text={created.token} />
          </div>
          <div className="ui-card__actions">
            <Button block size="lg" onClick={() => setCreated(null)}>
              I've stored it
            </Button>
          </div>
        </div>
      )}
      <Loader state={state}>
        {(d) => {
          const pats = d.tokens.filter((t) => t.kind === 'pat' && !t.revokedAt && !revoked.has(t.id));
          const now = new Date();
          const today = ymdInHomeZone(now);
          return (
            <ul className="ui-group" aria-label={pats.length ? 'Tokens' : undefined}>
              {pats.map((t) => (
                <li key={t.id} className="ui-swipe">
                  <div className="ui-row">
                    <span className="ui-row__lead">
                      <IconTile name="shield" color="#8e8e93" />
                    </span>
                    <span className="ui-row__main">
                      <span className="ui-row__text">
                        <span className="ui-row__title">{t.label}</span>
                        <span className="ui-row__sub">
                          <span className="lo-code">{t.prefix}…</span> · {t.scopes.join(' ')} ·{' '}
                          {t.projectIds?.length
                            ? t.projectIds.map(projectName).join(', ')
                            : t.spheres?.length
                              ? t.spheres.map((s) => sphereName(spheres, s)).join(', ')
                              : 'all spheres'}
                        </span>
                        <span className="ui-row__sub">
                          Used {since(t.lastUsedAt, now)}
                          {t.expiresAt && ` · expires ${stampWithYear(t.expiresAt, today)}`}
                        </span>
                      </span>
                    </span>
                    <span className="ui-row__trail">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={revoking.busy.has(t.id)}
                        onClick={() => void revoke(t)}
                      >
                        {revoking.busy.has(t.id) ? 'Revoking…' : 'Revoke'}
                      </Button>
                    </span>
                  </div>
                </li>
              ))}
              {pats.length === 0 && (
                <li className="ui-swipe">
                  <div className="ui-row ui-row--muted">
                    <span className="ui-row__main">
                      <span className="ui-row__title">No tokens yet.</span>
                    </span>
                  </div>
                </li>
              )}
              {!created && (
                <NewToken
                  spheres={spheres}
                  projects={projects}
                  onCreated={(c) => {
                    setCreated(c);
                    void state.reload();
                  }}
                />
              )}
            </ul>
          );
        }}
      </Loader>
    </section>
  );
}

function NewToken({
  spheres,
  projects,
  onCreated,
}: {
  spheres: Sphere[];
  projects: Project[];
  onCreated: (c: { token: string; record: TokenRecord }) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<GrantableScope[]>(['read']);
  const [sphereKeys, setSphereKeys] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [days, setDays] = useState<number | null>(90);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!label.trim()) return setErr('Give the token a name you will recognise.');
    if (scopes.length === 0) return setErr('Pick at least one permission.');
    setBusy(true);
    setErr(null);
    try {
      const res = await createToken({
        label: label.trim(),
        scopes,
        spheres: projectIds.length || sphereKeys.length === 0 ? null : sphereKeys,
        projectIds: projectIds.length ? projectIds : null,
        expiresInDays: days,
      });
      setLabel('');
      setScopes(['read']);
      setSphereKeys([]);
      setProjectIds([]);
      setOpen(false);
      onCreated(res);
    } catch (error) {
      setErr(errorText(error));
    } finally {
      setBusy(false);
    }
  }

  const openRow = (
    <li className="ui-swipe">
      <button type="button" className="ui-more bc-addrow" onClick={() => setOpen(true)}>
        <Icon name="plus" size={20} />
        Create a token
      </button>
    </li>
  );
  if (!open) return openRow;

  return (
    <>
      {openRow}
      <Sheet title="New token" onClose={() => setOpen(false)}>
        <form className="bc-sheetform" onSubmit={submit} aria-label="Create a token">
          <ul className="ui-group bc-inset">
            <li className="ui-swipe">
              <label className="bc-field">
                <span className="bc-field__label">Name</span>
                <input className="bc-field__input" value={label} placeholder="Claude Code laptop" onChange={(e) => setLabel(e.target.value)} />
              </label>
            </li>
            <li className="ui-swipe">
              <label className="bc-field">
                <span className="bc-field__label">Expires</span>
                <select
                  className="bc-field__input"
                  value={days === null ? '' : String(days)}
                  onChange={(e) => setDays(e.target.value === '' ? null : Number(e.target.value))}
                >
                  {EXPIRY.map((x) => (
                    <option key={x.label} value={x.days === null ? '' : String(x.days)}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </label>
            </li>
          </ul>
          <fieldset className="bc-fieldset">
            <legend className="bc-legend">Permissions</legend>
            <ul className="ui-group bc-inset">
              {SCOPES.map((s) => (
                <li key={s.key} className="ui-swipe">
                  <label className="bc-field">
                    <span className="bc-field__text">
                      {s.label}
                      <span className="bc-field__hint">{s.hint}</span>
                    </span>
                    <input type="checkbox" className="bc-switch" checked={scopes.includes(s.key)} onChange={() => setScopes((x) => toggle(x, s.key))} />
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          <fieldset className="bc-fieldset" disabled={projectIds.length > 0}>
            <legend className="bc-legend">Spheres {sphereKeys.length === 0 && <span className="bc-legend__hint">none on = all</span>}</legend>
            <ul className="ui-group bc-inset">
              {spheres.map((s) => (
                <li key={s.key} className="ui-swipe">
                  <label className="bc-field">
                    <span className="bc-field__text">{s.name}</span>
                    <input type="checkbox" className="bc-switch" checked={sphereKeys.includes(s.key)} onChange={() => setSphereKeys((x) => toggle(x, s.key))} />
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {projects.length > 0 && (
            <fieldset className="bc-fieldset">
              <legend className="bc-legend">
                Only these projects <span className="bc-legend__hint">for an agent runner; spheres follow</span>
              </legend>
              <ul className="ui-group bc-inset">
                {projects.map((p) => (
                  <li key={p.id} className="ui-swipe">
                    <label className="bc-field">
                      <span className="bc-field__text">{p.name}</span>
                      <input type="checkbox" className="bc-switch" checked={projectIds.includes(p.id)} onChange={() => setProjectIds((x) => toggle(x, p.id))} />
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
          {err && (
            <p className="bc-formerr" role="alert">
              {err}
            </p>
          )}
          <Button type="submit" size="lg" block disabled={busy}>
            {busy ? 'Creating…' : 'Create token'}
          </Button>
        </form>
      </Sheet>
    </>
  );
}

// --- OAuth clients ------------------------------------------------------------------------------------

function Clients({ state }: { state: Loaded<ConnectionsPayload> }): JSX.Element {
  const toast = useToast();
  const [revoked, setRevoked] = useState<ReadonlySet<string>>(new Set());
  const revoking = useBusy<string>();

  function revoke(c: OAuthClientRow): Promise<void> {
    const name = c.redirectHosts[0] ?? c.clientName ?? 'app';
    return revoking.run(c.clientId, async () => {
      try {
        await revokeClient(c.clientId);
        setRevoked((s) => new Set(s).add(c.clientId));
        toast.info(`Disconnected ${name}`);
        void state.reload();
      } catch (err) {
        toast.error(`Couldn't disconnect ${name}: ${errorText(err)}`);
      }
    });
  }

  return (
    <section className="ui-groupwrap" aria-labelledby="conn-apps">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="conn-apps">
          Connected apps
        </h2>
      </div>
      <Loader state={state}>
        {(d) => {
          const rows = d.oauthClients.filter((c) => !revoked.has(c.clientId));
          if (rows.length === 0) return <p className="ui-group__foot bc-foot">No apps connected yet.</p>;
          const now = new Date();
          return (
            <ul className="ui-group">
              {rows.map((c) => (
                <li key={c.clientId} className="ui-swipe">
                  <div className="ui-row">
                    <span className="ui-row__lead">
                      <IconTile name="plug" color="#d97757" />
                    </span>
                    <span className="ui-row__main">
                      <span className="ui-row__text">
                        <span className="ui-row__title">{c.redirectHosts.join(', ') || 'Unknown app'}</span>
                        <span className="ui-row__sub">
                          self-reported name: {c.clientName ?? 'none'} · {c.liveTokens} live {c.liveTokens === 1 ? 'token' : 'tokens'}
                        </span>
                        <span className="ui-row__sub">Used {since(c.lastUsedAt, now)}</span>
                      </span>
                    </span>
                    <span className="ui-row__trail">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={revoking.busy.has(c.clientId)}
                        onClick={() => void revoke(c)}
                      >
                        {revoking.busy.has(c.clientId) ? 'Disconnecting…' : 'Disconnect'}
                      </Button>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          );
        }}
      </Loader>
    </section>
  );
}

// --- Mail / calendar accounts ----------------------------------------------------------------------------

const PROVIDER_LABEL: Record<string, string> = {
  gmail: 'Gmail',
  gcal: 'Google Calendar',
  microsoft: 'Microsoft',
  msmail: 'Outlook mail',
  mscal: 'Outlook calendar',
};

/**
 * The connect callbacks land back here as
 * `/connections?connector=<gmail|gcal|microsoft>&result=connected|error|denied`.
 * Say how it went once, then drop the query so a reload doesn't repeat it.
 */
function useConnectResult(): void {
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const seen = useRef(false);
  useEffect(() => {
    if (seen.current) return;
    const label = PROVIDER_LABEL[params.get('connector') ?? ''];
    const result = params.get('result');
    if (!label || !result) return;
    seen.current = true;
    if (result === 'connected') toast.info(`${label} connected`);
    else if (result === 'denied') toast.info(`${label} not connected: access was declined`);
    else toast.error(`Couldn't connect ${label}. Try again.`);
    const next = new URLSearchParams(params);
    next.delete('connector');
    next.delete('result');
    next.set('tab', 'accounts'); // stay where the account is listed
    setParams(next, { replace: true });
  }, [params, setParams, toast]);
}

type AddKind = 'microsoft' | 'google' | 'imap' | 'icloud' | 'ics';

const PROVIDER_ICON: Record<string, { icon: 'mail' | 'calendar'; color: string }> = {
  gmail: { icon: 'mail', color: '#ea4335' },
  imap: { icon: 'mail', color: '#5a6b7d' },
  msmail: { icon: 'mail', color: '#0078d4' },
  gcal: { icon: 'calendar', color: '#1a73e8' },
  mscal: { icon: 'calendar', color: '#0078d4' },
  caldav: { icon: 'calendar', color: '#8a8f96' },
  ics: { icon: 'calendar', color: '#b07b2c' },
};

/**
 * Accounts: every mailbox and calendar in two lists, then one big "Add" list.
 * Each account carries a label, a job (sphere) and, for calendars Vitals can
 * write to, the "Vitals can add events here" switch.
 */
function Accounts(): JSX.Element {
  const connectors = useLoad(getConnectors, []);
  const spheres = useSpheres();
  const toast = useToast();
  const [adding, setAdding] = useState<AddKind | null>(null);
  const [editing, setEditing] = useState<Connector | null>(null);
  useConnectResult();

  function added(what: string): void {
    setAdding(null);
    toast.info(`${what} added`);
    connectors.reload();
  }

  async function setWritable(c: Connector, writable: boolean): Promise<void> {
    try {
      await updateConnector(c.id, { writable });
      toast.info(writable ? `Vitals can add events to ${c.label || c.account}` : `Vitals won't add events to ${c.label || c.account}`);
      connectors.reload();
    } catch (err) {
      toast.error(`Couldn't change that: ${errorText(err)}`);
    }
  }

  return (
    <section className="ui-groupwrap cx-accounts" aria-labelledby="conn-accounts">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="conn-accounts">
          Accounts
        </h2>
      </div>
      <Loader state={connectors}>
        {(d) => {
          const mail = d.items.filter(isMail);
          const cals = d.items.filter(isCalendar);
          const row = (c: Connector): JSX.Element => (
            <AccountRow key={c.id} c={c} spheres={spheres} onEdit={() => setEditing(c)} onWritable={(w) => void setWritable(c, w)} />
          );
          return (
            <>
              <Group header="Mail" label="Mail accounts">
                {mail.length ? mail.map(row) : <Row title="No mailboxes yet" tone="muted" />}
              </Group>
              <Group header="Calendars" label="Calendars">
                {cals.length ? cals.map(row) : <Row title="No calendars yet" tone="muted" />}
              </Group>
              <Group header="Add an account" label="Add an account">
                <AddRow
                  icon="mail"
                  color="#0078d4"
                  title="Microsoft (Outlook / work)"
                  sub="Mail and calendar, e.g. AllAboard365"
                  hint={d.microsoft ? null : 'Needs the Microsoft app set up — see docs'}
                  onClick={() => setAdding('microsoft')}
                />
                <AddRow
                  icon="mail"
                  color="#ea4335"
                  title="Google (Gmail + Calendar)"
                  sub="Sign in with Google"
                  hint={d.google ? null : 'Needs Google credentials on the server'}
                  onClick={() => setAdding('google')}
                />
                <AddRow icon="mail" color="#5a6b7d" title="Email (IMAP / cPanel)" sub="Any mailbox with a password, e.g. Rova Web" onClick={() => setAdding('imap')} />
                <AddRow icon="calendar" color="#8a8f96" title="iCloud Calendar" sub="Your personal calendar; Vitals can add to it" onClick={() => setAdding('icloud')} />
                <AddRow icon="calendar" color="#b07b2c" title="Calendar link (ICS)" sub="Read-only, from any calendar's share link" onClick={() => setAdding('ics')} />
              </Group>
              <Group header="Messages" label="Signal">
                <Row
                  leading={<IconTile name="chat" color="#3a76f0" />}
                  title="Signal"
                  sub={signalStatus({ gmail: false, gcal: false, signal: false, ...d.configured })}
                />
              </Group>
            </>
          );
        }}
      </Loader>
      {adding === 'microsoft' && <MicrosoftSheet spheres={spheres} onClose={() => setAdding(null)} />}
      {adding === 'google' && <GoogleSheet onClose={() => setAdding(null)} />}
      {adding === 'imap' && <ImapSheet spheres={spheres} onClose={() => setAdding(null)} onAdded={() => added('Mailbox')} />}
      {adding === 'icloud' && <IcloudSheet spheres={spheres} onClose={() => setAdding(null)} onAdded={() => added('iCloud Calendar')} />}
      {adding === 'ics' && <IcsSheet spheres={spheres} onClose={() => setAdding(null)} onAdded={() => added('Calendar link')} />}
      {editing && (
        <EditAccountSheet
          c={editing}
          spheres={spheres}
          onClose={() => setEditing(null)}
          onSaved={(what) => {
            setEditing(null);
            toast.info(what);
            connectors.reload();
          }}
        />
      )}
    </section>
  );
}

function AccountRow({
  c,
  spheres,
  onEdit,
  onWritable,
}: {
  c: Connector;
  spheres: Sphere[];
  onEdit: () => void;
  onWritable: (w: boolean) => void;
}): JSX.Element {
  const look = PROVIDER_ICON[c.provider] ?? { icon: 'mail' as const, color: '#8a8f96' };
  const title = c.label || c.account;
  const status = statusText(c, (iso) => since(iso, new Date()));
  return (
    <li className="ui-swipe cx-acct" aria-label={`Account ${title}`}>
      <div className="ui-row" style={c.sphereKey ? { boxShadow: `inset 3px 0 0 ${sphereColor(spheres, c.sphereKey)}` } : undefined}>
        <span className="ui-row__lead">
          <IconTile name={look.icon} color={look.color} />
        </span>
        <span className="ui-row__main">
          <span className="ui-row__text">
            <span className="ui-row__title">{title}</span>
            <span className="ui-row__sub">
              {c.label ? `${c.account} · ` : ''}
              {providerName(c.provider)}
              {c.sphereKey && (
                <>
                  {' · '}
                  <span className="cx-job" style={{ color: sphereColor(spheres, c.sphereKey) }}>
                    {sphereName(spheres, c.sphereKey)}
                  </span>
                </>
              )}
            </span>
            <span className="ui-row__sub">
              <span className={c.status === 'active' ? 'bc-good' : c.lastError ? 'bc-bad' : undefined}>{status}</span>
              {c.lastError && <span className="cx-err"> · {c.lastError}</span>}
            </span>
          </span>
        </span>
        <span className="ui-row__trail">
          <Button variant="secondary" size="sm" onClick={onEdit} aria-label={`Edit ${title}`}>
            Edit
          </Button>
        </span>
      </div>
      {canWrite(c) && (
        <label className="bc-field cx-acct__toggle">
          <span className="bc-field__text">Vitals can add events here</span>
          <input type="checkbox" className="bc-switch" role="switch" checked={c.writable} onChange={(e) => onWritable(e.target.checked)} />
        </label>
      )}
    </li>
  );
}

function AddRow({
  icon,
  color,
  title,
  sub,
  hint,
  onClick,
}: {
  icon: 'mail' | 'calendar';
  color: string;
  title: string;
  sub: string;
  hint?: string | null;
  onClick: () => void;
}): JSX.Element {
  const off = Boolean(hint);
  return (
    <li className="ui-swipe">
      <button type="button" className={`ui-row cx-add${off ? ' cx-add--off' : ''}`} disabled={off} onClick={onClick}>
        <span className="ui-row__lead">
          <IconTile name={icon} color={color} />
        </span>
        <span className="ui-row__main">
          <span className="ui-row__text">
            <span className="ui-row__title">{title}</span>
            <span className="ui-row__sub">{hint ?? sub}</span>
          </span>
        </span>
        {!off && <Icon name="plus" size={20} className="cx-add__plus" />}
      </button>
    </li>
  );
}

/** Job choices: active spheres, with a default. */
function JobField({
  spheres,
  value,
  onChange,
  allowNone = false,
}: {
  spheres: Sphere[];
  value: string;
  onChange: (k: string) => void;
  allowNone?: boolean;
}): JSX.Element {
  const live = spheres.filter((s) => !s.archived);
  return (
    <li className="ui-swipe">
      <label className="bc-field">
        <span className="bc-field__label">Job</span>
        <select className="bc-field__input" value={value} onChange={(e) => onChange(e.target.value)}>
          {allowNone && <option value="">None</option>}
          {live.length === 0 && !allowNone && <option value={value}>{value}</option>}
          {live.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
    </li>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email' | 'password' | 'url' | 'number';
  placeholder?: string;
  hint?: ReactNode;
  autoComplete?: string;
}): JSX.Element {
  return (
    <li className="ui-swipe">
      <label className="bc-field">
        <span className="bc-field__text">
          {label}
          {hint && <span className="bc-field__hint">{hint}</span>}
        </span>
        <input
          className="bc-field__input"
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete ?? (type === 'password' ? 'new-password' : 'off')}
          spellCheck={false}
          autoCapitalize="none"
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    </li>
  );
}

function FormFoot({ err, busy, label, busyLabel }: { err: string | null; busy: boolean; label: string; busyLabel: string }): JSX.Element {
  return (
    <>
      {err && (
        <p className="bc-formerr" role="alert">
          {err}
        </p>
      )}
      <Button type="submit" size="lg" block disabled={busy}>
        {busy ? busyLabel : label}
      </Button>
    </>
  );
}

const firstKey = (spheres: Sphere[], want: string): string =>
  spheres.find((s) => s.key === want && !s.archived)?.key ?? spheres.find((s) => !s.archived)?.key ?? want;

function MicrosoftSheet({ spheres, onClose }: { spheres: Sphere[]; onClose: () => void }): JSX.Element {
  const live = spheres.filter((s) => !s.archived);
  return (
    <PickSheet
      title="Which job is this Microsoft account for?"
      label="Job for Microsoft account"
      value={null}
      options={live.map((s) => ({ key: s.key, name: s.name, color: s.color ?? undefined }))}
      onPick={(k) => {
        if (k) nav.go(microsoftStartUrl(k));
        onClose();
      }}
      onClose={onClose}
    />
  );
}

function GoogleSheet({ onClose }: { onClose: () => void }): JSX.Element {
  const toast = useToast();
  const [starting, setStarting] = useState(false);
  async function connect(provider: 'gmail' | 'gcal'): Promise<void> {
    setStarting(true);
    try {
      const { authUrl } = await startConnector(provider);
      nav.go(authUrl);
    } catch (err) {
      toast.error(`Couldn't start: ${errorText(err)}`);
      setStarting(false);
    }
  }
  return (
    <Sheet title="Connect Google" onClose={onClose}>
      <p className="cx-sheet__lead">Gmail and Google Calendar are signed in one at a time.</p>
      <div className="cx-sheet__buttons">
        <Button size="lg" block icon="mail" disabled={starting} onClick={() => void connect('gmail')}>
          Connect Gmail
        </Button>
        <Button size="lg" block variant="secondary" icon="calendar" disabled={starting} onClick={() => void connect('gcal')}>
          Connect Google Calendar
        </Button>
      </div>
    </Sheet>
  );
}

function ImapSheet({ spheres, onClose, onAdded }: { spheres: Sphere[]; onClose: () => void; onAdded: () => void }): JSX.Element {
  const [label, setLabel] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [host, setHost] = useState('');
  const [hostEdited, setHostEdited] = useState(false);
  const [port, setPort] = useState('993');
  const [secure, setSecure] = useState(true);
  const [job, setJob] = useState(() => firstKey(spheres, 'rova'));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const effectiveHost = hostEdited ? host : defaultImapHost(email);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!email.trim().includes('@')) return setErr('Enter the email address.');
    if (!password) return setErr('Enter the mailbox password.');
    if (!effectiveHost.trim()) return setErr('Enter the mail server, e.g. mail.example.com.');
    const p = Number(port);
    if (!Number.isInteger(p) || p < 1 || p > 65535) return setErr('Port must be a number, usually 993.');
    setBusy(true);
    setErr(null);
    const pw = password;
    setPassword(''); // never kept in the form after it is sent
    try {
      await addImap({
        label: label.trim() || email.trim(),
        host: effectiveHost.trim(),
        port: p,
        secure,
        user: email.trim(),
        password: pw,
        sphereKey: job,
      });
      onAdded();
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Add email (IMAP)" onClose={onClose}>
      <form className="bc-sheetform" onSubmit={submit} aria-label="Add email account">
        <ul className="ui-group bc-inset">
          <TextField label="Name" value={label} onChange={setLabel} placeholder="Rova Web — Amar" />
          <TextField label="Email" type="email" value={email} onChange={setEmail} placeholder="amar@rovaweb.com" />
          <TextField label="Password" type="password" value={password} onChange={setPassword} hint="Sent once; never shown again" />
          <TextField
            label="Mail server"
            value={effectiveHost}
            onChange={(v) => {
              setHostEdited(true);
              setHost(v);
            }}
            placeholder="mail.example.com"
          />
          <TextField label="Port" type="number" value={port} onChange={setPort} />
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">Use SSL</span>
              <input type="checkbox" className="bc-switch" checked={secure} onChange={(e) => setSecure(e.target.checked)} />
            </label>
          </li>
          <JobField spheres={spheres} value={job} onChange={setJob} />
        </ul>
        <FormFoot err={err} busy={busy} label="Add mailbox" busyLabel="Checking the login…" />
      </form>
    </Sheet>
  );
}

function IcloudSheet({ spheres, onClose, onAdded }: { spheres: Sphere[]; onClose: () => void; onAdded: () => void }): JSX.Element {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [job, setJob] = useState(() => firstKey(spheres, 'personal'));
  const [writable, setWritable] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!user.trim()) return setErr('Enter your Apple ID email.');
    if (!password) return setErr('Enter an app-specific password.');
    setBusy(true);
    setErr(null);
    const pw = password;
    setPassword('');
    try {
      await addCaldav({ label: 'iCloud', user: user.trim(), password: pw, sphereKey: job, writable });
      onAdded();
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Add iCloud Calendar" onClose={onClose}>
      <form className="bc-sheetform" onSubmit={submit} aria-label="Add iCloud calendar">
        <p className="cx-sheet__lead">
          Apple needs an app-specific password, not your normal one. Make one at{' '}
          <a href="https://appleid.apple.com" target="_blank" rel="noreferrer noopener">
            appleid.apple.com
          </a>{' '}
          → Sign-In and Security → App-Specific Passwords, then paste it here.
        </p>
        <ul className="ui-group bc-inset">
          <TextField label="Apple ID" type="email" value={user} onChange={setUser} placeholder="you@icloud.com" />
          <TextField label="App-specific password" type="password" value={password} onChange={setPassword} placeholder="xxxx-xxxx-xxxx-xxxx" />
          <JobField spheres={spheres} value={job} onChange={setJob} />
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">Vitals can add events here</span>
              <input type="checkbox" className="bc-switch" checked={writable} onChange={(e) => setWritable(e.target.checked)} />
            </label>
          </li>
        </ul>
        <FormFoot err={err} busy={busy} label="Add iCloud Calendar" busyLabel="Checking…" />
      </form>
    </Sheet>
  );
}

function IcsSheet({ spheres, onClose, onAdded }: { spheres: Sphere[]; onClose: () => void; onAdded: () => void }): JSX.Element {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [job, setJob] = useState(() => firstKey(spheres, 'personal'));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!/^(https?|webcal):\/\//i.test(url.trim())) return setErr('Paste the calendar link (starts with https:// or webcal://).');
    setBusy(true);
    setErr(null);
    try {
      await addIcs({ label: label.trim() || 'Calendar', url: url.trim(), sphereKey: job });
      onAdded();
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Add a calendar link" onClose={onClose}>
      <form className="bc-sheetform" onSubmit={submit} aria-label="Add calendar link">
        <ul className="ui-group bc-inset">
          <TextField label="Name" value={label} onChange={setLabel} placeholder="Team calendar" />
          <TextField label="Link" type="url" value={url} onChange={setUrl} placeholder="https://…/calendar.ics" />
          <JobField spheres={spheres} value={job} onChange={setJob} />
        </ul>
        <FormFoot err={err} busy={busy} label="Add calendar" busyLabel="Adding…" />
      </form>
    </Sheet>
  );
}

function EditAccountSheet({
  c,
  spheres,
  onClose,
  onSaved,
}: {
  c: Connector;
  spheres: Sphere[];
  onClose: () => void;
  onSaved: (what: string) => void;
}): JSX.Element {
  const [label, setLabel] = useState(c.label ?? '');
  const [job, setJob] = useState(c.sphereKey ?? '');
  const [writable, setWritable] = useState(c.writable);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const name = c.label || c.account;

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    const patch: ConnectorPatch = {};
    if (label.trim() !== (c.label ?? '')) patch.label = label.trim();
    if ((job || null) !== c.sphereKey) patch.sphereKey = job || null;
    if (canWrite(c) && writable !== c.writable) patch.writable = writable;
    if (Object.keys(patch).length === 0) return onClose();
    setBusy(true);
    setErr(null);
    try {
      await updateConnector(c.id, patch);
      onSaved(`Saved ${label.trim() || c.account}`);
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await removeConnector(c.id);
      onSaved(`Removed ${name}`);
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title={name} onClose={onClose}>
      <form className="bc-sheetform" onSubmit={save} aria-label={`Edit ${name}`}>
        <p className="cx-sheet__lead">
          {providerName(c.provider)} · {c.account}
        </p>
        <ul className="ui-group bc-inset">
          <TextField label="Name" value={label} onChange={setLabel} placeholder={c.account} />
          <JobField spheres={spheres} value={job} onChange={setJob} allowNone />
          {canWrite(c) && (
            <li className="ui-swipe">
              <label className="bc-field">
                <span className="bc-field__text">Vitals can add events here</span>
                <input type="checkbox" className="bc-switch" checked={writable} onChange={(e) => setWritable(e.target.checked)} />
              </label>
            </li>
          )}
        </ul>
        <FormFoot err={err} busy={busy} label="Save" busyLabel="Saving…" />
      </form>
      <div className="cx-remove">
        {confirm ? (
          <>
            <p className="cx-remove__ask">Remove {name}? Vitals stops syncing it and forgets its password.</p>
            <div className="cx-remove__row">
              <Button variant="secondary" onClick={() => setConfirm(false)}>
                Keep it
              </Button>
              <Button variant="destructive" disabled={busy} onClick={() => void remove()}>
                Yes, remove
              </Button>
            </div>
          </>
        ) : (
          <Button variant="secondary" block onClick={() => setConfirm(true)}>
            Remove account
          </Button>
        )}
      </div>
    </Sheet>
  );
}

/**
 * The Signal row. `signalSend` says whether briefs and reminders can go out at
 * all; `signal` is inbound only (replies are read). A server that doesn't send
 * `signalSend` yet is not claimed to send.
 */
export function signalStatus(c: ConnectorsPayload['configured']): string {
  if (c.signalSend === false) return 'Not set up — briefs stay on the web';
  const out = c.signalSend === true ? 'Briefs and reminders go out over Signal' : 'Briefs go out over Signal once the server has it set up';
  return `${out} · ${c.signal ? 'replies are read' : 'replies are not read yet'}`;
}

// --- Spheres ------------------------------------------------------------------------------------------

const KEY_RE = /^[a-z][a-z0-9_-]{0,31}$/;

/** A sphere key from a name: 'Job 2' → 'job-2'. */
export function sphereKeyFrom(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/, '')
    .slice(0, 32);
}

/** A hex colour an `<input type="color">` accepts (#rrggbb), else a neutral grey. */
function colorInput(c: string | null): string {
  if (c && /^#[0-9a-fA-F]{6}$/.test(c)) return c.toLowerCase();
  if (c && /^#[0-9a-fA-F]{3}$/.test(c)) return `#${[...c.slice(1)].map((x) => x + x).join('')}`.toLowerCase();
  return '#8a8f96';
}

/**
 * Spheres (decision D1: renamable here). Their names reach Claude's
 * instructions, consent checkboxes and briefs, so a wrong name is fixed here
 * rather than by hand. Personal is the default sphere and cannot be archived.
 */
function Spheres(): JSX.Element {
  const list = useLoad(listSpheres, []);
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  function changed(): void {
    resetSphereCache();
    void list.reload();
  }

  return (
    <section className="ui-groupwrap" aria-labelledby="conn-spheres">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="conn-spheres">
          Spheres
        </h2>
        <button type="button" className="ui-group__action" onClick={() => setAdding(true)}>
          Add sphere
        </button>
      </div>
      <Loader state={list}>
        {(items) => (
          <ul className="ui-group" aria-label="Spheres">
            {[...items]
              .sort((a, b) => Number(a.archived) - Number(b.archived) || a.sort - b.sort)
              .map((s) => (
                <SphereRow key={`${s.key}:${s.updatedAt}`} sphere={s} onSaved={changed} onError={(m) => toast.error(m)} />
              ))}
          </ul>
        )}
      </Loader>
      <p className="ui-group__foot">Names reach Claude, briefs and consent screens. Tap a colour or a name to change it.</p>
      {adding && (
        <NewSphere
          taken={(list.data ?? []).map((s) => s.key)}
          onClose={() => setAdding(false)}
          onAdded={(s) => {
            setAdding(false);
            toast.info(`Added ${s.name}`);
            changed();
          }}
        />
      )}
    </section>
  );
}

function SphereRow({ sphere, onSaved, onError }: { sphere: Sphere; onSaved: () => void; onError: (m: string) => void }): JSX.Element {
  const [name, setName] = useState(sphere.name);
  const [color, setColor] = useState(colorInput(sphere.color));
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== sphere.name || color !== colorInput(sphere.color);
  const canArchive = sphere.key !== 'personal';

  async function save(patch: { name?: string; color?: string; archived?: boolean }, what: string): Promise<void> {
    setBusy(true);
    try {
      await updateSphere(sphere.key, patch);
      onSaved();
    } catch (err) {
      onError(`Couldn't ${what} ${sphere.name}: ${errorText(err)}`);
      setBusy(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!name.trim()) return onError('A sphere needs a name.');
    const patch: { name?: string; color?: string } = {};
    if (name.trim() !== sphere.name) patch.name = name.trim();
    if (color !== colorInput(sphere.color)) patch.color = color;
    void save(patch, 'save');
  }

  return (
    <li className={`ui-swipe bc-sphere${sphere.archived ? ' bc-sphere--archived' : ''}`}>
      <form className="bc-sphere__form" onSubmit={submit} aria-label={`Sphere ${sphere.key}`}>
        <input
          type="color"
          className="bc-swatch"
          value={color}
          aria-label={`Colour for ${sphere.name}`}
          onChange={(e) => setColor(e.target.value)}
          disabled={busy}
        />
        <div className="bc-sphere__main">
          <input
            className="bc-sphere__name"
            value={name}
            maxLength={60}
            aria-label={`Name for ${sphere.key}`}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <span className="bc-sphere__key">
            {sphere.key}
            {sphere.archived && ' · archived'}
          </span>
        </div>
        {dirty ? (
          <Button type="submit" size="sm" disabled={busy}>
            Save
          </Button>
        ) : (
          canArchive && (
            <Button
              variant="plain"
              size="sm"
              disabled={busy}
              onClick={() => void save({ archived: !sphere.archived }, sphere.archived ? 'restore' : 'archive')}
            >
              {sphere.archived ? 'Restore' : 'Archive'}
            </Button>
          )
        )}
      </form>
    </li>
  );
}

function NewSphere({
  taken,
  onClose,
  onAdded,
}: {
  taken: readonly string[];
  onClose: () => void;
  onAdded: (s: Sphere) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);
  const [color, setColor] = useState('#5aa9e6');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const effectiveKey = keyEdited ? key : sphereKeyFrom(name);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim()) return setErr('Give the sphere a name.');
    if (!KEY_RE.test(effectiveKey)) return setErr('Key: lowercase letters, digits, - or _, starting with a letter.');
    if (taken.includes(effectiveKey)) return setErr(`There is already a sphere called ${effectiveKey}.`);
    setBusy(true);
    setErr(null);
    try {
      onAdded(await createSphere({ key: effectiveKey, name: name.trim(), color }));
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title="New sphere" onClose={onClose}>
      <form className="bc-sheetform" onSubmit={submit} aria-label="Add a sphere">
        <ul className="ui-group bc-inset">
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Name</span>
              <input className="bc-field__input" value={name} maxLength={60} placeholder="Job 3" onChange={(e) => setName(e.target.value)} />
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">
                Key
                <span className="bc-field__hint">Used in links and by Claude; can't change later</span>
              </span>
              <input
                className="bc-field__input"
                value={effectiveKey}
                maxLength={32}
                onChange={(e) => {
                  setKeyEdited(true);
                  setKey(e.target.value);
                }}
              />
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">Colour</span>
              <input type="color" className="bc-swatch" value={color} onChange={(e) => setColor(e.target.value)} />
            </label>
          </li>
        </ul>
        {err && (
          <p className="bc-formerr" role="alert">
            {err}
          </p>
        )}
        <Button type="submit" size="lg" block disabled={busy}>
          {busy ? 'Adding…' : 'Add sphere'}
        </Button>
      </form>
    </Sheet>
  );
}

// --- Notifications (proactive settings) -------------------------------------------------------------

const BRIEFS: readonly { key: keyof ProactiveSettings['briefs']; label: string }[] = [
  { key: 'morning', label: 'Morning brief, weekdays 7:30' },
  { key: 'weekend', label: 'Weekend morning, 9:00' },
  { key: 'evening', label: 'Evening shutdown, weekdays 6:00 PM' },
  { key: 'weekly', label: 'Weekly review, Sunday 6:00 PM' },
  { key: 'monthly', label: 'Monthly money, the 1st' },
];

const HEALTH: readonly { key: keyof ProactiveSettings['healthNudges']; label: string }[] = [
  { key: 'water', label: 'Water reminders' },
  { key: 'move', label: 'Move after 90 idle minutes' },
  { key: 'windDown', label: 'Wind down at 9:45 PM' },
];

function Notifications(): JSX.Element {
  const settings = useLoad(getProactiveSettings, []);
  return (
    <section className="bc-notify" aria-labelledby="conn-notify">
      <h2 className="bc-bigsection" id="conn-notify">
        Notifications
      </h2>
      <Loader state={settings}>
        {(s) => <NotificationsForm key={JSON.stringify(s)} initial={s} onSaved={() => void settings.reload()} />}
      </Loader>
    </section>
  );
}

function NotificationsForm({ initial, onSaved }: { initial: ProactiveSettings; onSaved: () => void }): JSX.Element {
  const toast = useToast();
  const [s, setS] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!isClock(s.quietHours.start) || !isClock(s.quietHours.end)) return setErr('Quiet hours need a start and an end time.');
    // The tax set-aside rate lives with Money (/money → Income) and is never sent from here.
    const patch = settingsPatch(initial, { ...s, taxSetAsideRate: initial.taxSetAsideRate });
    if (Object.keys(patch).length === 0) return toast.info('Nothing changed');
    setBusy(true);
    setErr(null);
    try {
      await updateProactiveSettings(patch);
      toast.info('Notifications saved');
      onSaved();
    } catch (error) {
      setErr(errorText(error));
    } finally {
      setBusy(false);
    }
  }

  const sw = (checked: boolean, onChange: (v: boolean) => void): JSX.Element => (
    <input type="checkbox" className="bc-switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  );

  return (
    <form className="bc-notify__form" onSubmit={save} aria-label="Notifications">
      <Group header="Quiet hours" footer="Nothing pings you between these times.">
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Quiet from</span>
            <input
              className="bc-field__input"
              type="time"
              value={s.quietHours.start}
              onChange={(e) => setS({ ...s, quietHours: { ...s.quietHours, start: e.target.value } })}
            />
          </label>
        </li>
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Quiet until</span>
            <input
              className="bc-field__input"
              type="time"
              value={s.quietHours.end}
              onChange={(e) => setS({ ...s, quietHours: { ...s.quietHours, end: e.target.value } })}
            />
          </label>
        </li>
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Pings per hour, at most</span>
            <input
              className="bc-field__input"
              type="number"
              min="0"
              max="20"
              value={s.maxPingsPerHour}
              onChange={(e) => setS({ ...s, maxPingsPerHour: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
        </li>
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Pings per day, at most</span>
            <input
              className="bc-field__input"
              type="number"
              min="0"
              max="50"
              value={s.maxPingsPerDay}
              onChange={(e) => setS({ ...s, maxPingsPerDay: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
        </li>
      </Group>
      <Group header="Briefs">
        {BRIEFS.map((b) => (
          <li key={b.key} className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">{b.label}</span>
              {sw(s.briefs[b.key], (v) => setS({ ...s, briefs: { ...s.briefs, [b.key]: v } }))}
            </label>
          </li>
        ))}
      </Group>
      <Group header="Inbox and health">
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Inbox pings</span>
            <select
              className="bc-field__input"
              value={s.inboxPings}
              onChange={(e) => setS({ ...s, inboxPings: e.target.value as ProactiveSettings['inboxPings'] })}
            >
              <option value="smart">Smart — urgent, family, clients</option>
              <option value="off">Off</option>
            </select>
          </label>
        </li>
        {HEALTH.map((h) => (
          <li key={h.key} className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">{h.label}</span>
              {sw(s.healthNudges[h.key], (v) => setS({ ...s, healthNudges: { ...s.healthNudges, [h.key]: v } }))}
            </label>
          </li>
        ))}
      </Group>
      {err && (
        <p className="bc-formerr" role="alert">
          {err}
        </p>
      )}
      <Button type="submit" size="lg" block disabled={busy}>
        {busy ? 'Saving…' : 'Save notifications'}
      </Button>
    </form>
  );
}

// --- Audit -------------------------------------------------------------------------------------------------

function RecentAccess(): JSX.Element {
  const audit = useLoad(() => listAudit({ limit: 30 }), []);
  return (
    <section className="ui-groupwrap" aria-labelledby="conn-audit">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="conn-audit">
          Recent access
        </h2>
      </div>
      <Loader state={audit}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState icon="history" title="No access recorded yet" />
          ) : (
            <ul className="ui-group">
              <Collapsible
                items={items}
                n={8}
                render={(a) => (
                  <li key={a.id} className="ui-swipe">
                    <div className="ui-row">
                      <span className="ui-row__lead">
                        <span
                          className={`bc-status${a.status === 'ok' ? ' bc-status--good' : a.status === 'denied' ? ' bc-status--bad' : ' bc-status--warn'}`}
                          aria-hidden="true"
                        />
                      </span>
                      <span className="ui-row__main">
                        <span className="ui-row__text">
                          <span className="ui-row__title lo-row__title" title={a.action}>
                            {auditLabel(a.action)}
                          </span>
                          <span className="ui-row__sub">
                            {a.actor} · {stamp(a.at)}
                          </span>
                        </span>
                        {a.status !== 'ok' && (
                          <span className={`ui-row__detail bc-detail-sm ${a.status === 'denied' ? 'bc-bad' : 'bc-warn'}`}>{a.status}</span>
                        )}
                      </span>
                    </div>
                  </li>
                )}
              />
            </ul>
          )
        }
      </Loader>
    </section>
  );
}
